import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowRight2,
  ExportSquare,
  Logout,
  MessageQuestion,
  Messages,
  ProfileCircle,
  Setting2,
  Setting4,
  Verify,
} from 'iconsax-react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { navigateTo } from 'utils/navigation'
import { getPlanName } from 'utils/plans'
import { URL_CONST } from 'utils/urlConsts'

import { goToCreditsBilling } from 'containers/Agents/creditNavigation'
import {
  selectCreditsUsed,
  selectIsUnlimitedCredits,
  selectTotalCredits,
} from 'containers/Agents/creditSelectors'
import { selectActualLicenses, selectUser, selectUserEmail } from 'containers/App/selectors'
import AccountSetup from 'containers/Navigation/AccountSetup'
import { selectHelpMenuItems } from 'containers/Navigation/HelpMenu/selectors'
import { selectAccountSetupVisibility } from 'containers/Navigation/selectors'
import { saveLastSeenProductRelease } from 'containers/NewRelease/actions'

import { ThemeSegmentedSelector } from 'components/lib/ThemeSegmentedSelector'

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

// Circular initials avatar (Figma Avatar 1:30895): solid navy circle with
// white initials, in both the light and dark top bar. The bar re-points
// --top-navigation-text to a dark foreground, so the initials use an explicit
// white here (not --top-navigation-text) to stay legible on the navy chip.
/* Deepfake Profiles centering (flex on the chip, letters as a shrink-wrapped
   child). line-height:0 on the chip kills the UA <button> line-box strut
   that was shoving the initials toward the bottom. */
const initialsChip = css`
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;

  span {
    line-height: 1;
    text-transform: uppercase;
  }
`

const Avatar = styled.button`
  width: 32px;
  height: 32px;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  appearance: none;
  border-radius: 50%;
  background: var(--platform-avatar-bg);
  color: var(--platform-avatar-text);
  font-family: Poppins, sans-serif;
  font-size: 16px;
  font-weight: 400;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;
  user-select: none;
  ${initialsChip}

  &:hover {
    box-shadow: 0 0 0 2px var(--color-elevated-mark);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border);
    outline-offset: 2px;
  }
`

// ---------------------------------------------------------------------------
// Dropdown panel
// ---------------------------------------------------------------------------

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: var(--z-index-navigation, 1000);
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: var(--color-elevated-solid);
  border: 1px solid var(--ds-blue-7);
  border-radius: 8px;
  box-shadow:
    0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -2px rgba(0, 0, 0, 0.1);
  width: 319px;
  padding: 20px 0 6px;
  /* Visible so the Help & Support flyout can sit to the left of the panel
     (overflow:hidden previously clipped it, which is why the hover menu
     never appeared). */
  overflow: visible;
  font-family: Poppins, sans-serif;
`

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: var(--ds-blue-7);
  flex-shrink: 0;
`

// User header — avatar + name/title/email row (Figma 107:10213).
const UserHeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
`

// 40x40 circular avatar duplicated inside the panel header (Figma node
// 1:32114). Pale-blue chip + navy initials is self-contained contrast, so it
// reads fine on both the light and dark panel background.
const PanelAvatar = styled.div`
  width: 40px;
  height: 40px;
  padding: 0;
  box-sizing: border-box;
  border-radius: 50%;
  background: var(--platform-avatar-bg);
  color: var(--platform-avatar-text);
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
  user-select: none;
  ${initialsChip}
`

const UserTextBlock = styled.div`
  min-width: 0;
  flex: 1;
`

const UserName = styled.div`
  font-weight: 500; /* Figma 107:10219 — Poppins Medium */
  font-size: 14px;
  line-height: 20px;
  color: var(--sidebar-item-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const UserTitle = styled.div`
  font-size: 12px; /* Figma 107:10221 — Poppins Regular 12px */
  line-height: 16px;
  color: var(--t-colors-gray-600); /* Figma t--colors-gray-600 (#8D8D9E) */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const UserEmail = styled.div`
  font-size: 12px; /* Figma 107:10223 — Poppins Regular 12px */
  line-height: 16px;
  color: var(--t-colors-gray-600); /* Figma t--colors-gray-600 (#8D8D9E) */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Plan line — own section, leading verify icon + plan name (Figma user
// panel "plan line"), replacing the old inline uppercase badge.
const PlanRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 18px;
  font-size: 14px; /* Figma 107:10242 — Poppins Medium 14px */
  line-height: 20px;
  font-weight: 500;
  color: var(--color-text);

  svg {
    flex-shrink: 0;
    color: var(--sidebar-item-text);
  }
`

// ---------------------------------------------------------------------------
// Credits progress bar
// ---------------------------------------------------------------------------

const CreditsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
`

const CreditsLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--color-text);
`

const CreditsTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background: rgba(48, 99, 253, 0.2); /* Figma opacity-primary-light, node 1:32125 */
  overflow: hidden;
`

const CreditsFill = styled.div`
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    var(--primary),
    #1929b4
  ); /* #1929b4: Figma Colors/Blue/800, node 1:32125 */
  transition: width 0.3s ease;
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
`

// Row 2 of the credits block — usage percentage + CTA (Figma node 1:32125).
const CreditsSubRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: var(--sub-text-color);
`

const GetMoreLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  color: var(--primary);
  cursor: pointer;
  font-family: Poppins, sans-serif;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`

// ---------------------------------------------------------------------------
// Menu items
// ---------------------------------------------------------------------------

// Leading-icon + label layout (Figma profile menu items).
const menuItemBase = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px; /* Figma 107:10248 — Poppins Regular 14px */
  line-height: 20px;
  font-family: Poppins, sans-serif;
  color: var(--color-text);
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;

  svg {
    flex-shrink: 0;
    color: var(--sidebar-item-text);
  }

  &:hover,
  &:focus-visible {
    /* Figma 107:10253 — Colors/Blue/100 (#eaefff), 10px radius. Same token
       the sidebar uses so dark mode stays a readable hover, not gray #e8e8e8. */
    background: var(--sidebar-item-hover-background);
    border-radius: 10px;
  }
`

const MenuGroup = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 6px;
`

const MenuItem = styled.button`
  ${menuItemBase}
`

// ---------------------------------------------------------------------------
// Help & Support hover flyout (Figma 107:10211 — opens to the left)
// ---------------------------------------------------------------------------

// Relative anchor so the flyout panel can position against the trigger row.
const SubMenu = styled.div`
  position: relative;
`

// Trigger row reuses menuItemBase but lays the label + chevron out in a row.
const SubMenuTrigger = styled.button`
  ${menuItemBase}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  ${({ $open }) =>
    $open &&
    css`
      background: var(--sidebar-item-hover-background);
      border-radius: 10px;
    `}

  .chevron {
    flex-shrink: 0;
    color: var(--sidebar-item-text);
  }
`

// Groups the leading icon + label on the left of SubMenuTrigger so the
// chevron stays pinned to the right (justify-content: space-between above).
const SubMenuTriggerLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  svg {
    flex-shrink: 0;
    color: var(--sidebar-item-text);
  }
`

// Portaled to document.body (Figma 107:10551). The layout column uses
// overflow:hidden, which clipped an in-tree flyout — same bug that made the
// hover menu look like it "opened to the bottom" as an accordion fallback.
const HELP_FLYOUT_WIDTH = 271
const HELP_FLYOUT_GAP = 8

const SubMenuPanel = styled.div`
  position: fixed;
  z-index: calc(var(--z-index-navigation, 1000) + 2);
  display: flex;
  flex-direction: column;
  width: ${HELP_FLYOUT_WIDTH}px;
  padding: 6px 0;
  background: var(--color-elevated-solid);
  border: 1px solid var(--ds-blue-7);
  border-radius: 8px;
  box-shadow:
    0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -2px rgba(0, 0, 0, 0.1);
  font-family: Poppins, sans-serif;

  /* Bridge the gap back to the trigger so hover does not drop. */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: -${HELP_FLYOUT_GAP}px;
    width: ${HELP_FLYOUT_GAP}px;
    height: 100%;
  }
`

const SubMenuTitle = styled.div`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--color-text);
`

const SubMenuItem = styled.button`
  ${menuItemBase}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 40px;
  padding: 6px 12px;
  border-radius: 6px;

  .help-leading {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    svg {
      flex-shrink: 0;
      color: var(--sidebar-item-text);
    }
  }

  .help-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .red-indication {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--negative-color);
    flex-shrink: 0;
  }

  .external-link {
    flex-shrink: 0;
    color: var(--sidebar-item-text);
  }
`

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDisplayName(user, email) {
  const first = user?.firstName || user?.first_name
  const last = user?.lastName || user?.last_name
  if (first && last) return `${first} ${last}`
  return user?.fullName || user?.name || email || ''
}

function getDisplayTitle(user) {
  return user?.title || user?.jobTitle || user?.job_title || null
}

function getInitials(user, email) {
  const first = user?.firstName || user?.first_name
  const last = user?.lastName || user?.last_name
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase()
  const name = user?.fullName || user?.name
  if (name) {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    if (parts[0]) return parts[0].slice(0, 2).toUpperCase()
  }
  if (email) return email.slice(0, 2).toUpperCase()
  return 'U'
}

// Stable no-op for the legacy AccountSetup callback protocol props we don't use.
const noop = () => {}

function formatPlanName(plan) {
  // licenses.plan is a numeric PLAN_TYPES enum — resolve to its name first.
  if (plan === undefined || plan === null) return null
  const name = getPlanName(plan)
  if (!name || name === 'N/A') return null
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Thousands-grouped credit counts (Figma "7,500 / 10,000").
function formatCredits(n) {
  if (n === null || n === undefined) return '—'
  return Number(n).toLocaleString('en-US')
}

// Figma 107:10551 — vuesax/discord (iconsax has no brand mark).
const DiscordIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.1.1 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.1 16.1 0 0 0-4.8 0c-.14-.34-.36-.76-.54-1.09a.1.1 0 0 0-.07-.03c-1.5.26-2.94.71-4.27 1.33a.09.09 0 0 0-.04.04C2.68 9.06 1.86 12.69 2.26 16.27c0 .02.01.04.03.05 1.8 1.32 3.53 2.12 5.24 2.65a.1.1 0 0 0 .1-.04c.4-.55.76-1.13 1.07-1.74a.1.1 0 0 0-.05-.13 10.7 10.7 0 0 1-1.52-.73.1.1 0 0 1-.01-.16c.1-.08.2-.16.3-.24a.1.1 0 0 1 .1-.01c3.18 1.45 6.64 1.45 9.79 0a.1.1 0 0 1 .1.01c.1.08.2.16.3.24a.1.1 0 0 1-.01.16c-.48.28-.99.52-1.52.73a.1.1 0 0 0-.05.13c.31.61.67 1.19 1.07 1.74a.1.1 0 0 0 .1.04c1.72-.53 3.45-1.33 5.25-2.65a.1.1 0 0 0 .03-.05c.48-4.15-.8-7.75-3.4-10.9a.08.08 0 0 0-.04-.04ZM8.52 14.37c-.96 0-1.75-.88-1.75-1.96 0-1.08.78-1.96 1.75-1.96s1.77.89 1.75 1.96c0 1.08-.78 1.96-1.75 1.96Zm6.97 0c-.96 0-1.75-.88-1.75-1.96 0-1.08.79-1.96 1.75-1.96s1.77.89 1.75 1.96c0 1.08-.78 1.96-1.75 1.96Z" />
  </svg>
)

// Figma 107:10551 — vuesax/outline/newspaper.
const NewspaperIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 6.5A1.5 1.5 0 0 1 5.5 5H16a2 2 0 0 1 2 2v11.5H5.5A1.5 1.5 0 0 1 4 17V6.5Z" />
    <path d="M18 8h1.5A1.5 1.5 0 0 1 21 9.5v8A1.5 1.5 0 0 1 19.5 19H8" />
    <path d="M7.5 9h6M7.5 12h6M7.5 15h3.5" />
  </svg>
)

// Leading glyphs from Figma 107:10551 (16px). Do not reuse the legacy Help
// menu set (globe / gift / single-bubble) — those are a different icon family.
function HelpLeadingIcon({ name }) {
  if (name === 'Help Center') {
    return <MessageQuestion size={16} variant="Outline" color="currentColor" />
  }
  if (name === 'IRONSCALES Discord') {
    return <DiscordIcon />
  }
  if (name === 'Latest Release Note') {
    return <NewspaperIcon />
  }
  if (name === 'Leave us a message' || name === 'Contact Support') {
    return <Messages size={16} variant="Outline" color="currentColor" />
  }
  return <MessageQuestion size={16} variant="Outline" color="currentColor" />
}

// ---------------------------------------------------------------------------
// Wrapper
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

// ---------------------------------------------------------------------------
// AccountSetup section (mounted inline; existing popup handles its own state)
// ---------------------------------------------------------------------------

/**
 * ProfileMenu — initials avatar + dropdown with:
 *   - User details (name / title / email)
 *   - Monthly-Credits progress bar + "Get More Credits" CTA
 *     (source: billing/status via selectCreditsUsed / selectTotalCredits;
 *      hidden when the total is unknown or the tenant is uncapped)
 *   - Company plan line (shield-check icon + plan name)
 *   - Profile / Account Setup / Account Settings (Figma 107:10211)
 *   - Help & Support — hover flyout to the left (Figma 107:9336 / 107:10551;
 *     same options as the legacy HelpMenu via selectHelpMenuItems)
 *   - Theme selector (ThemeSegmentedSelector: Light / Dark / System). The previous
 *     two-state pill could only set light or dark, so a user already on System could
 *     never get back to it; this reuses the control the legacy nav already exposes.
 *   - Logout
 *
 * AC9 — all of the above are present.
 *
 * @param {object}  props
 * @param {string} [props.className]
 */
const ProfileMenu = ({ className }) => {
  const [open, setOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [helpPos, setHelpPos] = useState(null)
  const wrapperRef = useRef(null)
  const helpTriggerRef = useRef(null)
  const helpFlyoutRef = useRef(null)
  const helpCloseTimer = useRef(null)
  // Legacy AccountSetup registers its popup-toggle here (IconItem protocol).
  const accountSetupToggleRef = useRef(null)
  const [accountSetupTodoCount, setAccountSetupTodoCount] = useState(0)
  const history = useHistory()
  const dispatch = useDispatch()

  const user = useSelector(selectUser)
  const email = useSelector(selectUserEmail)
  const licenses = useSelector(selectActualLicenses)
  const accountSetupVisible = useSelector(selectAccountSetupVisibility)
  // Credit balance derives from billing/status (containers/Agents/creditSelectors).
  const creditsUsed = useSelector(selectCreditsUsed)
  const totalCredits = useSelector(selectTotalCredits)
  const isUnlimitedCredits = useSelector(selectIsUnlimitedCredits)
  // Reuse the legacy Help menu data (labels / destinations / gating) verbatim.
  const helpMenuItems = useSelector(selectHelpMenuItems)

  const initials = useMemo(() => getInitials(user, email), [user, email])
  const planLabel = useMemo(() => formatPlanName(licenses?.plan), [licenses])

  // Credits bar — hidden when the balance is unknown (not fetched / no tenant
  // record) or the tenant is uncapped (a used/total counter is meaningless).
  const creditsPct = totalCredits > 0 ? Math.round((creditsUsed / totalCredits) * 100) : 0
  const hasCredits = totalCredits != null && !isUnlimitedCredits

  const close = useCallback(() => {
    if (helpCloseTimer.current) {
      clearTimeout(helpCloseTimer.current)
      helpCloseTimer.current = null
    }
    setOpen(false)
    setHelpOpen(false)
  }, [])

  const openHelp = useCallback(() => {
    if (helpCloseTimer.current) {
      clearTimeout(helpCloseTimer.current)
      helpCloseTimer.current = null
    }
    setHelpOpen(true)
  }, [])

  const scheduleCloseHelp = useCallback(() => {
    if (helpCloseTimer.current) clearTimeout(helpCloseTimer.current)
    helpCloseTimer.current = setTimeout(() => setHelpOpen(false), 160)
  }, [])

  useEffect(
    () => () => {
      if (helpCloseTimer.current) clearTimeout(helpCloseTimer.current)
    },
    []
  )

  useEffect(() => {
    const handler = (e) => {
      const inMenu = wrapperRef.current && wrapperRef.current.contains(e.target)
      const inFlyout = helpFlyoutRef.current && helpFlyoutRef.current.contains(e.target)
      if (!inMenu && !inFlyout) close()
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, close])

  // Pin the portaled flyout to the Help row (left of the trigger, Figma 107:9336).
  useLayoutEffect(() => {
    if (!helpOpen) {
      setHelpPos(null)
      return undefined
    }
    const place = () => {
      const trigger = helpTriggerRef.current
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      const left = rect.left - HELP_FLYOUT_GAP - HELP_FLYOUT_WIDTH
      setHelpPos({
        top: rect.top,
        left: left < HELP_FLYOUT_GAP ? rect.right + HELP_FLYOUT_GAP : left,
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [helpOpen])

  const navigate = (path, state) => {
    close()
    history.push(path, state)
  }

  const handleLogout = () => {
    close()
    navigateTo(URL_CONST.login.signout)
  }

  // Mirrors the legacy HelpMenu click behavior: the release-note item marks the
  // release as seen, every item opens its URL in a new tab.
  const handleHelpItemClick = useCallback(
    (item) => {
      if (item.name === 'Latest Release Note') dispatch(saveLastSeenProductRelease())
      window.open(item.url, '_blank')
      close()
    },
    [dispatch, close]
  )

  const handleHelpKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setHelpOpen((v) => !v)
      } else if (e.key === 'Escape' && helpOpen) {
        e.preventDefault()
        e.stopPropagation()
        setHelpOpen(false)
      }
    },
    [helpOpen]
  )

  const displayName = getDisplayName(user, email)
  const displayTitle = getDisplayTitle(user)

  return (
    <Wrapper className={className} ref={wrapperRef}>
      <Avatar
        id="top-bar-profile-avatar"
        type="button"
        data-pendo-feature="nav-profile-open"
        aria-label="Open profile menu"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        <span>{initials}</span>
      </Avatar>

      {open && (
        <Panel id="top-bar-profile-panel" role="dialog" aria-label="Profile menu">
          {/* User details */}
          <UserHeaderSection>
            <PanelAvatar aria-hidden="true">
              <span>{initials}</span>
            </PanelAvatar>
            <UserTextBlock>
              <UserName id="profile-menu-user-name" title={displayName}>
                {displayName}
              </UserName>
              {displayTitle && (
                <UserTitle id="profile-menu-user-title" title={displayTitle}>
                  {displayTitle}
                </UserTitle>
              )}
              <UserEmail id="profile-menu-user-email" title={email}>
                {email}
              </UserEmail>
            </UserTextBlock>
          </UserHeaderSection>

          {hasCredits && (
            <>
              <Divider />
              <CreditsSection id="profile-menu-credits-section">
                <CreditsLabel>
                  <span>Monthly Credits</span>
                  <span id="profile-menu-credits-count">
                    {formatCredits(creditsUsed)} / {formatCredits(totalCredits)}
                  </span>
                </CreditsLabel>
                <CreditsTrack>
                  <CreditsFill
                    id="profile-menu-credits-bar"
                    $pct={creditsPct}
                    aria-valuenow={creditsPct}
                    role="progressbar"
                    aria-label="Monthly credits used"
                  />
                </CreditsTrack>
                <CreditsSubRow>
                  <span id="profile-menu-credits-pct">{creditsPct}% used</span>
                  <GetMoreLink
                    id="profile-menu-get-more-credits"
                    type="button"
                    data-pendo-feature="nav-profile-get-more-credits"
                    onClick={() => {
                      close()
                      goToCreditsBilling(history)
                    }}
                  >
                    Get More Credits
                  </GetMoreLink>
                </CreditsSubRow>
              </CreditsSection>
            </>
          )}

          {planLabel && (
            <>
              <Divider />
              <PlanRow id="profile-menu-plan-section">
                <Verify size={20} variant="Bold" color="currentColor" />
                <span id="profile-menu-plan">{planLabel}</span>
              </PlanRow>
            </>
          )}

          <Divider />
          <MenuGroup>
            <MenuItem
              id="profile-menu-profile-link"
              type="button"
              data-pendo-feature="nav-profile-profile"
              onClick={() => navigate(URL_CONST.settings.profile, { openSettingsNav: true })}
            >
              <ProfileCircle size={16} variant="Bold" color="currentColor" />
              <span>Profile</span>
            </MenuItem>
            {accountSetupVisible && (
              <>
                <MenuItem
                  id="profile-menu-account-setup"
                  type="button"
                  data-pendo-feature="nav-profile-account-setup"
                  onClick={() => accountSetupToggleRef.current && accountSetupToggleRef.current()}
                >
                  <Setting4 size={16} variant="Outline" color="currentColor" />
                  <span>
                    Account Setup
                    {accountSetupTodoCount ? ` (${accountSetupTodoCount})` : ''}
                  </span>
                </MenuItem>
                <AccountSetup
                  setOnClickCallback={(toggle) => {
                    accountSetupToggleRef.current = toggle
                  }}
                  setBadgeCount={setAccountSetupTodoCount}
                  setDropdownShown={noop}
                  onCloseCallback={noop}
                  /* NavigationPopup defaults to "center under a 46px top-bar
                     icon" (top:54px, right: -177px). Inside this panel that
                     drops the 400px card on top of the menu. Pin it to the
                     left of the profile panel instead (Figma 107:9336). */
                  popupTop="0"
                  popupRight="calc(100% + 8px)"
                />
              </>
            )}
            <MenuItem
              id="profile-menu-account-settings-link"
              type="button"
              data-pendo-feature="nav-profile-account-settings"
              onClick={() => navigate(URL_CONST.settings.general, { openSettingsNav: true })}
            >
              <Setting2 size={16} variant="Outline" color="currentColor" />
              <span>Account Settings</span>
            </MenuItem>
            <SubMenu onMouseEnter={openHelp} onMouseLeave={scheduleCloseHelp}>
              <SubMenuTrigger
                ref={helpTriggerRef}
                id="profile-menu-help-link"
                type="button"
                data-pendo-feature="nav-profile-help"
                $open={helpOpen}
                aria-haspopup="menu"
                aria-expanded={helpOpen}
                onClick={openHelp}
                onKeyDown={handleHelpKeyDown}
              >
                <SubMenuTriggerLabel>
                  <MessageQuestion size={16} variant="Outline" color="currentColor" />
                  <span>Help &amp; Support</span>
                </SubMenuTriggerLabel>
                <ArrowRight2 className="chevron" size={16} variant="Outline" color="currentColor" />
              </SubMenuTrigger>
            </SubMenu>
            {helpOpen &&
              helpPos &&
              createPortal(
                <SubMenuPanel
                  ref={helpFlyoutRef}
                  id="profile-menu-help-submenu"
                  role="menu"
                  aria-label="Help & Support"
                  style={{ top: helpPos.top, left: helpPos.left }}
                  onMouseEnter={openHelp}
                  onMouseLeave={scheduleCloseHelp}
                >
                  <SubMenuTitle>What can we do for you?</SubMenuTitle>
                  {helpMenuItems.map((item) => (
                    <SubMenuItem
                      key={item.name}
                      id={`profile-menu-help-item-${item.name
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-|-$/g, '')}`}
                      type="button"
                      role="menuitem"
                      data-pendo-feature="nav-profile-help-item"
                      onClick={() => handleHelpItemClick(item)}
                    >
                      <span className="help-leading">
                        <HelpLeadingIcon name={item.name} />
                        <span className="help-label">{item.name}</span>
                        {item.showRedIndication && <span className="red-indication" />}
                      </span>
                      <ExportSquare
                        className="external-link"
                        size={16}
                        variant="Outline"
                        color="currentColor"
                      />
                    </SubMenuItem>
                  ))}
                </SubMenuPanel>,
                document.body
              )}
            <ThemeSegmentedSelector />
          </MenuGroup>

          <Divider />
          <MenuGroup>
            <MenuItem
              id="profile-menu-logout"
              type="button"
              data-pendo-feature="nav-profile-logout"
              onClick={handleLogout}
            >
              <Logout size={16} variant="Outline" color="currentColor" />
              <span>Logout</span>
            </MenuItem>
          </MenuGroup>
        </Panel>
      )}
    </Wrapper>
  )
}

export default ProfileMenu
